import React from 'react'
import { Table, Button, Tag, Badge, Space } from 'antd'
import { EyeOutlined, CheckOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Notification } from '../types/notification.types'

interface NotificationsTableProps {
    notifications: Notification[]
    onRowClick: (notification: Notification) => void
    onRetry: (notification: Notification) => void
}

export const NotificationsTable: React.FC<NotificationsTableProps> = ({
    notifications,
    onRowClick,
    onRetry
}) => {
    const columns: ColumnsType<Notification> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <strong>{id}</strong>,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => {
                const colors = {
                    info: 'blue',
                    success: 'green',
                    warning: 'orange',
                    error: 'red',
                }
                return <Tag color={colors[type as keyof typeof colors]}>{type.toUpperCase()}</Tag>
            },
        },
        {
            title: 'Recipient',
            dataIndex: 'recipient',
            key: 'recipient',
        },
        {
            title: 'Channel',
            dataIndex: 'channel',
            key: 'channel',
            render: (channel: string) => {
                const icons = {
                    email: '📧',
                    sms: '💬',
                    push: '🔔',
                    'in-app': '📱',
                }
                return (
                    <span>
                        {icons[channel as keyof typeof icons]} {channel.toUpperCase()}
                    </span>
                )
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const statusMap = {
                    sent: { status: 'success' as const, text: 'SENT' },
                    pending: { status: 'processing' as const, text: 'PENDING' },
                    failed: { status: 'error' as const, text: 'FAILED' },
                    read: { status: 'success' as const, text: 'READ' },
                }
                const config = statusMap[status as keyof typeof statusMap]
                return <Badge status={config.status} text={config.text} />
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => onRowClick(record)}
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        icon={<CheckOutlined />}
                        disabled={record.status !== 'pending' && record.status !== 'failed'}
                        onClick={(e) => {
                            e.stopPropagation()
                            onRetry(record)
                        }}
                    >
                        Retry
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={notifications}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: notifications.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} notifications`,
            }}
        />
    )
}
